import { useEffect, useState } from 'react';
import { useLoaderData, useNavigate, useRevalidator, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Avatar, Badge, Button, Icon } from '@ds/core';
import { Alert, EmptyState } from '@ds/feedback';
import { SearchField } from '@ds/forms';
import { Tabs } from '@ds/navigation';
import { PageHeader } from '@/comuns/PageHeader';
import { dataEHoraPorExtenso, horaDe } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { indisponibilidadeDoProvedor } from '@/data/integracoes';
import { dados } from '@/data/porta';
import type { CargaDaCaixa, Conversa, Mensagem, TipoDeCanal } from '@/data/tipos';
import type { CargaDeAtendimentoECrm } from '@/data/cadastros';
import { casaNoFiltro, conversasDaAba, reservadaParaOutro, situacaoDaConversa } from '@/data/comunicacao';
import type { AbaDaComunicacao, QuemVeACaixa, SelecaoDoFiltro, SinaisDaConversa } from '@/data/comunicacao';
import { atual } from '@/session/sessao';
import { CanaisModal } from './comunicacao/CanaisModal';
import { CompositorDaConversa } from './comunicacao/CompositorDaConversa';
import { ChipsDoFiltro, FiltroInteligente } from './comunicacao/FiltroInteligente';
import type { GrupoDeOpcoes } from './comunicacao/FiltroInteligente';
import { PainelDoCliente } from './comunicacao/PainelDoCliente';
import { ResolverConversaModal } from './comunicacao/ResolverConversaModal';
import { TransferirConversaModal } from './comunicacao/TransferirConversaModal';

interface CargaDaTela {
    aba: AbaDaComunicacao;
    /** A caixa já recortada pela aba. */
    caixa: CargaDaCaixa;
    /** A caixa inteira (com a busca): é dela que saem os contadores das abas. */
    todas: Conversa[];
    conversaAtiva: Conversa | null;
    mensagens: Mensagem[];
    sinais: Record<string, SinaisDaConversa>;
    cadastros: CargaDeAtendimentoECrm;
    /** Para o recorte das reservas da fila (0161). */
    quem: QuemVeACaixa;
}

/** As três abas (17/09/2026). A situação é calculada: `situacaoDaConversa`. */
const ABAS: { id: AbaDaComunicacao; rotulo: string }[] = [
    { id: 'abertos', rotulo: 'Abertos' },
    { id: 'pendentes', rotulo: 'Pendentes' },
    { id: 'resolvidos', rotulo: 'Resolvidos' },
];

function abaDaRota(pathname: string): AbaDaComunicacao {
    const ultimo = pathname.split('/').pop() ?? '';
    return ABAS.find((a) => a.id === ultimo)?.id ?? 'abertos';
}

export async function carregarComunicacao({ request }: LoaderFunctionArgs): Promise<CargaDaTela> {
    const url = new URL(request.url);
    const aba = abaDaRota(url.pathname);
    const filtro = {
        busca: url.searchParams.get('q') ?? undefined,
        canalId: url.searchParams.get('canal') ?? undefined,
        fila: url.searchParams.get('fila') ?? undefined,
    };

    /* A caixa vem inteira e as abas se calculam sobre ela: os contadores das
       três abas saem do mesmo retrato. */
    const [inteira, sinais, cadastros] = await Promise.all([
        dados().caixaDeEntrada({ aba: 'todas', ...filtro }),
        dados().sinaisDaCaixa(),
        dados().atendimentoECrm(),
    ]);
    const quem: QuemVeACaixa = { nome: atual()?.nome, gestao: cadastros.podeGestao };
    const caixa: CargaDaCaixa = { ...inteira, conversas: conversasDaAba(inteira.conversas, aba, quem) };

    /* A conversa pedida na URL manda, mesmo que esteja em outra aba (aceitar
       uma pendente não pode tirá-la da tela); sem pedido, a primeira da aba.

       Pedido que não se cumpre NÃO cai na primeira: quem chega aqui vindo de
       "Enviar mensagem" tem uma pessoa em mente, e abrir outra conversa em
       silêncio faria escrever para quem não era. */
    const pedida = url.searchParams.get('conversa');
    const conversaAtiva = pedida
        ? inteira.conversas.find((c) => c.id === pedida) ?? null
        : caixa.conversas[0] ?? null;

    return {
        aba,
        caixa,
        todas: inteira.conversas,
        conversaAtiva,
        mensagens: conversaAtiva ? await dados().mensagensDaConversa(conversaAtiva.id) : [],
        sinais,
        cadastros,
        quem,
    };
}

const CONDUZ: Record<Conversa['conduz'], { rotulo: string; tom: 'success' | 'warning' | 'info' | 'brand'; icone: string }> = {
    ia: { rotulo: 'Assistente', tom: 'brand', icone: 'sparkles' },
    humano: { rotulo: 'Pediu uma pessoa', tom: 'warning', icone: 'hand' },
    equipe: { rotulo: 'Equipe', tom: 'success', icone: 'user-check' },
};

const TIPO_DO_CANAL: Record<TipoDeCanal, string> = {
    whatsapp: 'WhatsApp',
    instagram: 'Instagram',
    email: 'E-mail',
    sms: 'SMS',
    webchat: 'Chat do site',
};

/** Tela estreita: uma coluna por vez (lista, conversa ou cliente). */
function useTelaEstreita(): boolean {
    const consulta = '(max-width: 760px)';
    const [estreita, setEstreita] = useState(() => typeof window !== 'undefined' && Boolean(window.matchMedia?.(consulta).matches));
    useEffect(() => {
        const mq = window.matchMedia?.(consulta);
        if (!mq) return;
        const mudou = () => setEstreita(mq.matches);
        mq.addEventListener?.('change', mudou);
        return () => mq.removeEventListener?.('change', mudou);
    }, []);
    return estreita;
}

export function Comunicacao() {
    const { aba, caixa, todas, conversaAtiva, mensagens, sinais, cadastros, quem } = useLoaderData() as CargaDaTela;
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const [confirmar, confirmacao] = useConfirmacao();
    const estreita = useTelaEstreita();

    const [canaisAbertos, setCanaisAbertos] = useState(false);
    const [transferindo, setTransferindo] = useState(false);
    const [resolvendo, setResolvendo] = useState(false);
    const [agindo, setAgindo] = useState(false);
    const [busca, setBusca] = useState(params.get('q') ?? '');
    const [selecao, setSelecao] = useState<SelecaoDoFiltro>({});
    const painelAberto = params.get('cliente') === '1';

    const trocarParam = (chave: string, valor: string | null) => {
        setParams((atuais) => {
            const proximos = new URLSearchParams(atuais);
            if (!valor) proximos.delete(chave);
            else proximos.set(chave, valor);
            return proximos;
        }, { replace: true });
    };

    /* Mesma razão da busca de Contatos: ligar o campo direto ao parâmetro faz
       cada tecla dar a volta pelo roteador, e letra se perde. */
    useEffect(() => {
        const atualQ = params.get('q') ?? '';
        if (busca === atualQ) return;
        const t = window.setTimeout(() => trocarParam('q', busca || null), 250);
        return () => window.clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [busca]);

    const irParaAba = (id: AbaDaComunicacao, conversaId?: string) => {
        const proximos = new URLSearchParams(params);
        if (conversaId) proximos.set('conversa', conversaId);
        navigate({ pathname: `/app/comunicacao/${id}`, search: proximos.toString() ? `?${proximos}` : '' });
    };

    const canalDaConversa = caixa.canais.find((c) => c.id === conversaAtiva?.canalId);
    const canalConectado = canalDaConversa?.status === 'conectado';
    const sinaisDaAtiva = conversaAtiva ? sinais[conversaAtiva.id] : undefined;
    const situacaoDaAtiva = conversaAtiva ? situacaoDaConversa(conversaAtiva) : null;
    const meuNome = atual()?.nome;
    const deOutraPessoa = Boolean(conversaAtiva?.atendente && meuNome && conversaAtiva.atendente !== meuNome);
    /* Pendente guardada pela fila para outra pessoa (0161): quem vê é a gestão, ou quem abriu pelo link. */
    const reservadaDeOutro = Boolean(conversaAtiva && situacaoDaAtiva === 'pendente' && reservadaParaOutro(conversaAtiva, { nome: meuNome, gestao: false }));
    const reservadaParaMim = Boolean(conversaAtiva?.reservadaPara && situacaoDaAtiva === 'pendente' && !reservadaDeOutro);
    /* Devolve quem atende, ou a gestão. */
    const podeDevolver = situacaoDaAtiva === 'aberto' && (!deOutraPessoa || quem.gestao);

    const contagem: Record<AbaDaComunicacao, number> = {
        abertos: conversasDaAba(todas, 'abertos', quem).length,
        pendentes: conversasDaAba(todas, 'pendentes', quem).length,
        resolvidos: conversasDaAba(todas, 'resolvidos', quem).length,
    };

    const grupos: GrupoDeOpcoes[] = [
        { id: 'leitura', rotulo: 'Leitura', icone: 'mail', opcoes: [{ id: 'nao-lidas', rotulo: 'Só com mensagem não lida' }] },
        {
            id: 'conduz', rotulo: 'Quem conduz', icone: 'bot',
            opcoes: [{ id: 'ia', rotulo: 'Assistente' }, { id: 'humano', rotulo: 'Pediu uma pessoa' }, { id: 'equipe', rotulo: 'Equipe' }],
        },
        { id: 'canal', rotulo: 'Canais', icone: 'radio', opcoes: caixa.canais.map((c) => ({ id: c.id, rotulo: c.nome, nota: TIPO_DO_CANAL[c.tipo] })) },
        { id: 'atendente', rotulo: 'Usuários', icone: 'users', opcoes: cadastros.membros.map((m) => ({ id: m.nome, rotulo: m.nome })) },
        { id: 'departamento', rotulo: 'Departamentos', icone: 'layers', opcoes: cadastros.departamentos.map((d) => ({ id: d.nome, rotulo: d.nome })) },
        { id: 'fila', rotulo: 'Filas', icone: 'list-ordered', opcoes: cadastros.filas.map((f) => ({ id: f.id, rotulo: f.nome })) },
        {
            id: 'procedimento', rotulo: 'Procedimentos', icone: 'clipboard-list',
            opcoes: [...new Set(Object.values(sinais).flatMap((s) => s.procedimentos))].sort((a, b) => a.localeCompare(b, 'pt-BR')).map((p) => ({ id: p, rotulo: p })),
        },
        { id: 'agendamento', rotulo: 'Agendamento', icone: 'calendar-days', opcoes: [{ id: 'com', rotulo: 'Com agendamento' }, { id: 'sem', rotulo: 'Sem agendamento' }] },
        { id: 'oportunidade', rotulo: 'Oportunidades', icone: 'target', opcoes: [{ id: 'sim', rotulo: 'Com oportunidade no CRM' }] },
        { id: 'proposta', rotulo: 'Propostas em aberto', icone: 'receipt', opcoes: [{ id: 'sim', rotulo: 'Aguardando resposta' }] },
        {
            id: 'etiqueta', rotulo: 'Etiquetas', icone: 'tag',
            opcoes: [
                { id: 'cadastro:Paciente', rotulo: 'Paciente' },
                { id: 'cadastro:Lead', rotulo: 'Lead' },
                ...cadastros.etiquetas.filter((e) => e.ativa).map((e) => ({ id: `etiqueta:${e.id}`, rotulo: e.nome })),
            ],
        },
    ];

    const lista = caixa.conversas.filter((c) => casaNoFiltro(c, sinais[c.id], selecao));
    const pediramPessoa = conversasDaAba(todas, 'pendentes', quem).filter((c) => c.conduz === 'humano').length;
    const recarregar = () => { void revalidator.revalidate(); };
    const canalSemIntegracao = canalDaConversa ? indisponibilidadeDoProvedor(canalDaConversa.provedor) : null;

    const falha = (titulo: string) => (e: unknown) => {
        aviso({ title: titulo, description: e instanceof Error ? e.message : undefined, tone: 'danger' });
    };

    const aceitar = async (c: Conversa) => {
        if (reservadaDeOutro) {
            const ok = await confirmar({
                titulo: `Aceitar a conversa reservada para ${c.reservadaParaNome ?? 'outra pessoa'}?`,
                descricao: `A fila guardou ${c.nome} para ${c.reservadaParaNome ?? 'outra pessoa'}. Aceitando, a conversa passa a ser sua e sai da lista dela.`,
                confirmar: 'Aceitar',
            });
            if (!ok) return;
        } else if (deOutraPessoa) {
            const ok = await confirmar({
                titulo: `Assumir a conversa de ${c.atendente ?? ''}?`,
                descricao: `${c.nome} passa a ser atendido por você. ${c.atendente ?? ''} deixa de ver a conversa como dele.`,
                confirmar: 'Assumir',
            });
            if (!ok) return;
        }
        setAgindo(true);
        try {
            await dados().assumirConversa(c.id);
            aviso({ title: deOutraPessoa ? 'Conversa assumida' : 'Atendimento aceito', description: c.nome });
            irParaAba('abertos', c.id);
        } catch (e) {
            falha('Não foi possível aceitar a conversa')(e);
        } finally {
            setAgindo(false);
        }
    };

    const reabrir = async (c: Conversa) => {
        setAgindo(true);
        try {
            await dados().reabrirConversa(c.id);
            aviso({ title: 'Conversa reaberta', description: `${c.nome} · agora com você` });
            irParaAba('abertos', c.id);
        } catch (e) {
            falha('Não foi possível reabrir a conversa')(e);
        } finally {
            setAgindo(false);
        }
    };

    const devolver = async (c: Conversa) => {
        setAgindo(true);
        try {
            await dados().devolverConversa(c.id);
            aviso({ title: 'Conversa devolvida para Pendentes', description: `${c.nome} · livre para quem é da fila` });
            irParaAba('pendentes', c.id);
        } catch (e) {
            falha('Não foi possível devolver a conversa')(e);
        } finally {
            setAgindo(false);
        }
    };

    /* No celular, uma coluna de cada vez: a lista, a conversa ou o cliente. */
    const vistaEstreita = !conversaAtiva || !params.get('conversa') ? 'lista' : painelAberto ? 'cliente' : 'conversa';
    const mostraLista = !estreita || vistaEstreita === 'lista';
    const mostraConversa = !estreita || vistaEstreita === 'conversa';
    const mostraPainel = Boolean(conversaAtiva && painelAberto) && (!estreita || vistaEstreita === 'cliente');
    const colunas = estreita
        ? 'minmax(0,1fr)'
        : mostraPainel
            ? 'minmax(240px, 300px) minmax(0,1fr) minmax(280px, 340px)'
            : 'minmax(260px, 340px) minmax(0,1fr)';

    let rodape;
    if (conversaAtiva && situacaoDaAtiva === 'pendente') {
        rodape = (
            <div role="region" aria-label="Aceitar o atendimento" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: 14, borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-secondary)' }}>
                <Icon name={CONDUZ[conversaAtiva.conduz].icone} size={16} />
                <span style={{ flex: 1, minWidth: 180, fontSize: 13, color: 'var(--text-body)' }}>
                    {reservadaParaMim
                        ? 'A fila distribuiu esta conversa para você. Aceite para responder.'
                        : reservadaDeOutro
                            ? `A fila reservou esta conversa para ${conversaAtiva.reservadaParaNome ?? 'outra pessoa'}. Ela ainda não aceitou.`
                            : conversaAtiva.conduz === 'ia'
                                ? 'A assistente está respondendo. Aceite para assumir a conversa e responder você.'
                                : 'Ninguém da equipe aceitou esta conversa ainda. Aceite para responder.'}
                </span>
                <Button iconLeft={<Icon name="check" size={15} />} loading={agindo} onClick={() => void aceitar(conversaAtiva)}>Aceitar atendimento</Button>
            </div>
        );
    } else if (conversaAtiva && situacaoDaAtiva === 'resolvido') {
        rodape = (
            <div role="region" aria-label="Conversa resolvida" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: 14, borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-secondary)' }}>
                <Icon name="circle-check" size={16} />
                <span style={{ flex: 1, minWidth: 180, fontSize: 13, color: 'var(--text-body)' }}>
                    Resolvida{conversaAtiva.resolvidaEm ? ` em ${dataEHoraPorExtenso(conversaAtiva.resolvidaEm)}` : ''}
                    {conversaAtiva.motivoResolucao ? ` · ${conversaAtiva.motivoResolucao}` : ''}.
                    {' '}Se a pessoa escrever de novo, volta para Pendentes.
                </span>
                <Button variant="secondary" iconLeft={<Icon name="rotate-ccw" size={15} />} loading={agindo} onClick={() => void reabrir(conversaAtiva)}>Reabrir</Button>
            </div>
        );
    } else if (conversaAtiva) {
        rodape = (
            <CompositorDaConversa
                conversa={conversaAtiva}
                canalConectado={canalConectado}
                canalSemIntegracao={canalSemIntegracao}
                sinais={sinaisDaAtiva}
                mensagensRapidas={cadastros.mensagensRapidas}
                onEnviada={recarregar}
            />
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%', minHeight: 0 }}>
            {pediramPessoa > 0 && (
                <Alert
                    tone="warning"
                    title={pediramPessoa === 1 ? '1 pessoa pediu atendimento da equipe' : `${pediramPessoa} pessoas pediram atendimento da equipe`}
                    action={(
                        <Button size="sm" variant="secondary" onClick={() => { setSelecao({ conduz: ['humano'] }); irParaAba('pendentes'); }}>
                            Ver em Pendentes
                        </Button>
                    )}
                >
                    A assistente não responde a quem pediu uma pessoa: estas conversas esperam alguém aceitar.
                </Alert>
            )}

            <PageHeader
                title="Comunicação"
                description={`${todas.length} conversas · ${todas.filter((c) => c.naoLidas > 0).length} com mensagem não lida`}
                actions={
                    <Button variant="secondary" iconLeft={<Icon name="radio" size={15} />} onClick={() => setCanaisAbertos(true)}>
                        Canais ({caixa.canais.length})
                    </Button>
                }
            >
                <Tabs
                    ariaLabel="Situação das conversas" activeId={aba}
                    onChange={(id) => navigate({ pathname: `/app/comunicacao/${id}`, search: params.toString() ? `?${params}` : '' })}
                    tabs={ABAS.map((a) => ({ id: a.id, label: a.rotulo, count: contagem[a.id] }))}
                />
            </PageHeader>

            <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: colunas, gap: 16 }}>
                {mostraLista && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                <SearchField value={busca} onChange={setBusca} placeholder="Buscar por nome ou mensagem" width="100%" />
                            </span>
                            <FiltroInteligente grupos={grupos} selecao={selecao} onChange={setSelecao} resultado={lista.length} />
                        </div>
                        <ChipsDoFiltro grupos={grupos} selecao={selecao} onChange={setSelecao} />

                        <div role="list" aria-label="Conversas" style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {lista.length === 0 && (
                                <EmptyState
                                    compact icon="message-square-off"
                                    title={aba === 'pendentes' ? 'Nada esperando' : aba === 'abertos' ? 'Nenhum atendimento aberto' : 'Nenhuma conversa resolvida'}
                                    description="Troque de aba, limpe a busca ou o filtro."
                                />
                            )}
                            {lista.map((c) => {
                                const ativa = c.id === conversaAtiva?.id;
                                const conduz = CONDUZ[c.conduz];
                                return (
                                    <button
                                        key={c.id} type="button" aria-current={ativa ? 'true' : undefined}
                                        onClick={() => trocarParam('conversa', c.id)}
                                        style={{
                                            display: 'flex', gap: 10, alignItems: 'flex-start', padding: 11, textAlign: 'left',
                                            border: `1px solid ${ativa ? 'var(--border-brand-soft)' : 'var(--border-subtle)'}`,
                                            background: ativa ? 'var(--surface-brand-soft)' : 'var(--surface)',
                                            borderRadius: 'var(--radius-md)', cursor: 'pointer', fontFamily: 'var(--font-ui)',
                                        }}
                                    >
                                        <Avatar name={c.nome} size="sm" />
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.nome}</span>
                                                {c.ultimaMensagemEm && (
                                                    <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{horaDe(c.ultimaMensagemEm)}</span>
                                                )}
                                            </span>
                                            <span style={{ display: 'block', marginTop: 2, fontSize: 12.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {c.ultimaMensagem ?? 'Sem mensagens'}
                                            </span>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                                                {aba === 'pendentes' ? <Badge tone={conduz.tom} dot>{conduz.rotulo}</Badge>
                                                    : c.atendente ? <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{c.atendente}</span> : null}
                                                {aba === 'pendentes' && c.reservadaPara && (
                                                    <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>
                                                        {c.reservadaParaNome && c.reservadaParaNome !== quem.nome ? `Para ${c.reservadaParaNome}` : 'Para você'}
                                                    </span>
                                                )}
                                                {c.fila && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{c.fila}</span>}
                                                {aba === 'resolvidos' && c.motivoResolucao && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{c.motivoResolucao}</span>}
                                                {c.naoLidas > 0 && (
                                                    <span aria-label={`${c.naoLidas} não lidas`} style={{ marginLeft: 'auto', minWidth: 18, height: 18, padding: '0 5px', borderRadius: 'var(--radius-pill)', background: 'var(--action-primary)', color: 'var(--action-primary-fg)', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        {c.naoLidas}
                                                    </span>
                                                )}
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {mostraConversa && (
                    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)' }}>
                        {!conversaAtiva ? (
                            <EmptyState
                                icon="message-circle"
                                title={params.get('conversa') ? 'Esta conversa não está no recorte atual' : 'Nenhuma conversa selecionada'}
                                description={params.get('conversa')
                                    ? 'Ela existe, mas a busca ou o filtro a escondem. Limpe o recorte para vê-la.'
                                    : 'Escolha uma conversa à esquerda para ler e responder.'}
                            />
                        ) : (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: 12, borderBottom: '1px solid var(--border-subtle)' }}>
                                    {estreita && (
                                        <Button size="sm" variant="ghost" iconLeft={<Icon name="arrow-left" size={14} />} onClick={() => trocarParam('conversa', null)}>Conversas</Button>
                                    )}
                                    {/* Clicar no nome abre a coluna do cliente (fase 2). */}
                                    <button
                                        type="button" onClick={() => trocarParam('cliente', painelAberto ? null : '1')}
                                        aria-expanded={painelAberto} aria-label={`Dados de ${conversaAtiva.nome}`}
                                        style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 160, padding: 4, border: 0, borderRadius: 'var(--radius-md)', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-ui)' }}
                                    >
                                        <Avatar name={conversaAtiva.nome} size="md" />
                                        <span style={{ minWidth: 0 }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                                                {conversaAtiva.nome}
                                                <Icon name={painelAberto ? 'panel-right-close' : 'panel-right-open'} size={14} />
                                            </span>
                                            <span style={{ display: 'block', marginTop: 2, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                                {conversaAtiva.canalNome}
                                                {conversaAtiva.atendente ? ` · ${conversaAtiva.atendente}` : ''}
                                                {conversaAtiva.fila ? ` · ${conversaAtiva.fila}` : ''}
                                            </span>
                                        </span>
                                    </button>
                                    {situacaoDaAtiva && (
                                        <Badge tone={situacaoDaAtiva === 'aberto' ? 'success' : situacaoDaAtiva === 'pendente' ? 'warning' : 'neutral'} dot>
                                            {situacaoDaAtiva === 'aberto' ? 'Aberto' : situacaoDaAtiva === 'pendente' ? 'Pendente' : 'Resolvido'}
                                        </Badge>
                                    )}
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} aria-label="Ações da conversa" role="toolbar">
                                        {situacaoDaAtiva === 'pendente' && (
                                            <Button size="sm" iconLeft={<Icon name="check" size={14} />} loading={agindo} onClick={() => void aceitar(conversaAtiva)}>Aceitar</Button>
                                        )}
                                        {situacaoDaAtiva === 'aberto' && deOutraPessoa && (
                                            <Button size="sm" variant="secondary" loading={agindo} onClick={() => void aceitar(conversaAtiva)}>Assumir</Button>
                                        )}
                                        {sinaisDaAtiva?.atendimentoDeHojeId ? (
                                            <Button
                                                variant="secondary" size="sm" iconLeft={<Icon name="stethoscope" size={14} />}
                                                onClick={() => navigate(`/app/atendimentos/editor/${sinaisDaAtiva.atendimentoDeHojeId ?? ''}`)}
                                            >
                                                Consulta de hoje
                                            </Button>
                                        ) : null}
                                        {podeDevolver && (
                                            <Button variant="secondary" size="sm" iconLeft={<Icon name="undo-2" size={14} />} loading={agindo} onClick={() => void devolver(conversaAtiva)}>
                                                Devolver para Pendentes
                                            </Button>
                                        )}
                                        {situacaoDaAtiva !== 'resolvido' && (
                                            <>
                                                <Button variant="secondary" size="sm" iconLeft={<Icon name="arrow-right-left" size={14} />} onClick={() => setTransferindo(true)}>
                                                    Transferir
                                                </Button>
                                                <Button variant="secondary" size="sm" iconLeft={<Icon name="circle-check" size={14} />} onClick={() => setResolvendo(true)}>
                                                    Resolver
                                                </Button>
                                            </>
                                        )}
                                        {situacaoDaAtiva === 'resolvido' && (
                                            <Button variant="secondary" size="sm" iconLeft={<Icon name="rotate-ccw" size={14} />} loading={agindo} onClick={() => void reabrir(conversaAtiva)}>
                                                Reabrir
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {/* `log` é o papel de uma thread que cresce: o leitor de
                                    tela anuncia a mensagem nova sem tirar o foco de onde
                                    a pessoa está digitando. */}
                                <section
                                    role="log" aria-label={`Conversa com ${conversaAtiva.nome}`}
                                    style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}
                                >
                                    {mensagens.length === 0 && (
                                        <EmptyState compact icon="message-square" title="Sem mensagens ainda" description="A conversa começa com a primeira resposta." />
                                    )}
                                    {mensagens.map((m) => {
                                        const nossa = m.direcao === 'enviada';
                                        return (
                                            <div key={m.id} style={{ display: 'flex', justifyContent: nossa ? 'flex-end' : 'flex-start' }}>
                                                <div
                                                    style={{
                                                        maxWidth: '72%', padding: '9px 12px', borderRadius: 'var(--radius-md)',
                                                        background: nossa ? 'var(--surface-brand-soft)' : 'var(--surface-secondary)',
                                                        border: `1px solid ${nossa ? 'var(--border-brand-soft)' : 'var(--border-subtle)'}`,
                                                    }}
                                                >
                                                    <p style={{ margin: 0, fontSize: 13.5, lineHeight: '19px', color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{m.texto}</p>
                                                    <p style={{ margin: '5px 0 0', fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                                                        {m.autor ? `${m.autor} · ` : ''}{dataEHoraPorExtenso(m.ocorridoEm)}
                                                        {/* "Pendente" é a verdade enquanto o canal não está
                                                            conectado: a mensagem existe no histórico e ainda
                                                            não saiu para o provedor. */}
                                                        {nossa && m.status === 'pendente' ? ' · aguardando o canal' : ''}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </section>

                                {rodape}
                            </>
                        )}
                    </div>
                )}

                {mostraPainel && conversaAtiva && (
                    <PainelDoCliente
                        key={conversaAtiva.id}
                        conversa={conversaAtiva} sinais={sinaisDaAtiva} cadastros={cadastros}
                        onFechar={() => trocarParam('cliente', null)}
                        onMudou={recarregar}
                    />
                )}
            </div>

            <CanaisModal open={canaisAbertos} canais={caixa.canais} onClose={() => setCanaisAbertos(false)} />
            {transferindo && conversaAtiva && (
                <TransferirConversaModal
                    conversa={conversaAtiva}
                    filaAtual={sinaisDaAtiva?.filaId}
                    cadastros={cadastros}
                    onFechar={() => setTransferindo(false)}
                    onTransferida={recarregar}
                />
            )}
            {resolvendo && conversaAtiva && (
                <ResolverConversaModal
                    conversa={conversaAtiva}
                    onFechar={() => setResolvendo(false)}
                    onResolvida={recarregar}
                />
            )}
            {confirmacao}
        </div>
    );
}
